import { useEffect, useState } from 'react'
import { Phone, User, Clock, X, Bot, AlertCircle, Star } from 'lucide-react'
import { useRingCentral } from '../../contexts/RingCentralContext'
import { supabase } from '../../lib/supabase'
import { dealLabel } from '../../lib/dealLabel'
import { getSessionWithTimeout } from '../../lib/authSession'
import { Link } from 'react-router-dom'
import { displayDate } from '../../lib/dateUtil.js'

function formatPhone(num) {
  if (!num) return ''
  const d = num.replace(/\D/g, '')
  if (d.length === 11 && d[0] === '1') return `+1 (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`
  return num
}

const CP_TIER_COLOR = { 1: 'text-red-600', 2: 'text-amber-600', 3: 'text-warm-500' }
const CP_TIER_LABEL = { 1: 'VIP', 2: 'Priority', 3: 'Active' }

function daysSince(dateStr) {
  if (!dateStr) return null
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000)
  if (diff === 0) return 'today'
  if (diff === 1) return 'yesterday'
  return `${diff}d ago`
}

export default function InboundCallPopup() {
  const { inboundCall, dismissInbound } = useRingCentral()
  const [recentActivity, setRecentActivity] = useState([])
  const [openDeals, setOpenDeals] = useState([])
  const [brief, setBrief] = useState(null)   // Janet pre-call brief
  const [briefLoading, setBriefLoading] = useState(false)
  const [ring, setRing] = useState(false)

  useEffect(() => {
    if (!inboundCall) {
      setRecentActivity([])
      setOpenDeals([])
      setBrief(null)
      return
    }
    setRing(true)
    const t = setTimeout(() => setRing(false), 600)

    if (inboundCall.contact?.id) {
      const contactId = inboundCall.contact.id

      // Fetch recent calls + open deals in parallel
      Promise.all([
        supabase
          .from('call_log')
          .select('contact_date, call_type, notes, summary, sentiment')
          .eq('contact_id', contactId)
          .order('contact_date', { ascending: false })
          .limit(3),
        supabase
          .from('deals')
          .select('id, title, deal_type, status, talent_name, talent_type, studio_name, counterpart')
          .contains('linked_contacts', [contactId])
          .in('status', ['active', 'tracking', 'negotiating'])
          .limit(3),
      ]).then(([callsRes, dealsRes]) => {
        setRecentActivity(callsRes.data || [])
        setOpenDeals(dealsRes.data || [])
      })

      // Ask Janet for a pre-call brief
      setBriefLoading(true)
      getSessionWithTimeout().then(({ data: { session } }) => {
        if (!session) { setBriefLoading(false); return }
        fetch('/api/meeting-brief', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            attendeeEmails: inboundCall.contact?.emails?.map(e => e.email).filter(Boolean) || [],
            eventTitle: `Inbound call from ${inboundCall.contact?.first_name || 'contact'}`,
          }),
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.talkingPoints?.length) setBrief(data.talkingPoints)
          })
          .catch(() => null)
          .finally(() => setBriefLoading(false))
      })
    }

    return () => clearTimeout(t)
  }, [inboundCall])

  if (!inboundCall) return null

  const { from, contact } = inboundCall
  const callerName = contact
    ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
    : from?.name || null
  const callerNumber = formatPhone(from?.phoneNumber || '')
  const cpTier = contact?.va_cp_tier
  const isVIP = cpTier === 1 || contact?.va_vip_bypass

  return (
    <div className={`fixed bottom-28 right-6 z-50 w-84 bg-white rounded-2xl shadow-2xl border-2 transition-all duration-300 ${
      ring ? 'border-green-400 scale-105' : isVIP ? 'border-amber-300' : 'border-green-200'
    }`} style={{ width: 320 }}>

      {/* Header */}
      <div className={`rounded-t-2xl px-4 py-3 flex items-center gap-3 border-b ${
        isVIP ? 'bg-amber-50 border-amber-100' : 'bg-green-50 border-green-100'
      }`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
          isVIP ? 'bg-amber-100' : 'bg-green-100'
        }`}>
          {contact
            ? <span className={`text-sm font-bold ${isVIP ? 'text-amber-700' : 'text-green-700'}`}>
                {(contact.first_name?.[0] || '?').toUpperCase()}
              </span>
            : <User size={18} className={isVIP ? 'text-amber-600' : 'text-green-600'} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-semibold text-warm-500 uppercase tracking-wide">
              {isVIP ? '⭐ VIP Inbound' : 'Incoming Call'}
            </p>
            {cpTier && (
              <span className={`text-[10px] font-bold ${CP_TIER_COLOR[cpTier] || 'text-warm-500'}`}>
                {CP_TIER_LABEL[cpTier] || ''}
              </span>
            )}
          </div>
          {callerName
            ? <p className="text-sm font-bold text-warm-900 truncate">{callerName}</p>
            : <p className="text-sm text-warm-500 italic">Unknown caller</p>}
          {callerNumber && <p className="text-xs text-warm-500">{callerNumber}</p>}
        </div>
        <button onClick={dismissInbound} className="text-warm-300 hover:text-warm-500 shrink-0">
          <X size={14} />
        </button>
      </div>

      <div className="px-4 py-3 space-y-3 max-h-72 overflow-y-auto">

        {/* Contact link + last contact */}
        {contact && (
          <div className="flex items-center justify-between">
            <Link
              to={`/contacts/${contact.id}`}
              className="text-xs text-navy-600 hover:text-navy-800 font-medium flex items-center gap-1"
            >
              <User size={11} /> View contact →
            </Link>
            {contact.last_contact_date && (
              <span className="text-[10px] text-warm-400">
                Last: {daysSince(contact.last_contact_date)}
              </span>
            )}
          </div>
        )}

        {/* Open deals */}
        {openDeals.length > 0 && (
          <div>
            <p className="text-[10px] text-warm-400 uppercase font-mono tracking-wide mb-1">Open deals</p>
            {openDeals.map(d => (
              <Link key={d.id} to={`/deals/${d.id}`}
                className="flex items-center gap-1.5 py-0.5 hover:text-navy-700 group"
              >
                <AlertCircle size={10} className="text-terra-400 shrink-0" />
                <span className="text-[11px] text-warm-700 group-hover:text-navy-700 truncate">
                  {dealLabel(d, { context: 'client' })} <span className="text-warm-400">({d.status})</span>
                </span>
              </Link>
            ))}
          </div>
        )}

        {/* Janet pre-call brief */}
        {briefLoading && (
          <div className="flex items-center gap-1.5">
            <Bot size={11} className="text-navy-400 animate-pulse" />
            <span className="text-[10px] text-warm-400 italic">Janet is preparing a brief…</span>
          </div>
        )}
        {brief && brief.length > 0 && (
          <div className="bg-navy-50 rounded-lg px-3 py-2">
            <p className="text-[10px] text-navy-400 uppercase font-mono tracking-wide mb-1.5 flex items-center gap-1">
              <Bot size={10} /> Janet's brief
            </p>
            <ul className="space-y-1">
              {brief.map((point, i) => (
                <li key={i} className="text-[11px] text-navy-700 flex items-start gap-1.5">
                  <span className="text-navy-300 shrink-0 mt-0.5">·</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recent activity */}
        {recentActivity.length > 0 && (
          <div>
            <p className="text-[10px] text-warm-400 uppercase font-mono tracking-wide mb-1">Recent calls</p>
            {recentActivity.map((a, i) => (
              <div key={i} className="flex items-start gap-1.5 py-0.5">
                <Clock size={10} className="text-warm-300 shrink-0 mt-0.5" />
                <span className="text-[11px] text-warm-600 truncate">
                  {displayDate(a.contact_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {a.sentiment && <span className="ml-1 text-warm-400">({a.sentiment})</span>}
                  {(a.notes || a.summary) ? ` — ${(a.notes || a.summary).slice(0, 45)}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {!contact && (
          <p className="text-[11px] text-warm-400 italic">Not in Chatrone</p>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-3 border-t border-warm-100 pt-2">
        <p className="text-[10px] text-warm-400 text-center">Answer or decline in the RingCentral widget →</p>
      </div>
    </div>
  )
}
